import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbPencil, TbUsersGroup } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { BaasGroupsSkeleton } from "../components/BaasSkeleton";
import { fetchBaasGroups } from "../../../../Services/baasUsersApi";
import { BaasGroupCreateModal } from "./components/BaasGroupCreateModal";

const roleLabelKey = {
  admin: "baas.users.role.admin",
  operator: "baas.users.role.operator",
  viewer: "baas.users.role.viewer",
};

const roleChipStyles = {
  admin: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  operator: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
  viewer: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

export const BaasGroupsPage = () => {
  const { instances, clearSelectedInstance } = useBaasInstance();
  const instancesKey = instances.map((item) => item.id).join("|");
  const isLoading = useBaasPageLoading(instancesKey || "org-groups");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [manageSupported, setManageSupported] = useState(false);

  // Org page — trust MS ManageSupported (users.manage). Viewers get false from portal.
  const canManageGroups = Boolean(manageSupported);

  const instanceNameById = useMemo(
    () => Object.fromEntries(instances.map((instance) => [instance.id, instance.name])),
    [instances],
  );

  useEffect(() => {
    clearSelectedInstance();
  }, [clearSelectedInstance]);

  useEffect(() => {
    let cancelled = false;

    if (!instancesKey) {
      setGroups([]);
      setManageSupported(false);
      setGroupsLoading(false);
      return undefined;
    }

    const orgInstances = instances;
    setGroupsLoading(true);
    (async () => {
      try {
        const result = await fetchBaasGroups(null, { instances: orgInstances });
        if (!cancelled) {
          setGroups(result.items);
          setManageSupported(Boolean(result.manageSupported));
        }
      } catch {
        if (!cancelled) {
          setGroups([]);
          setManageSupported(false);
        }
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [instancesKey, refreshKey]);

  const handleModalClose = () => {
    setCreateModalOpen(false);
    setEditingGroup(null);
  };

  const handleSaved = () => {
    setRefreshKey((current) => current + 1);
  };

  if (isLoading || groupsLoading) {
    return <BaasGroupsSkeleton count={3} />;
  }

  return (
    <div className="baas-groups min-w-0 space-y-5">
      <header className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.grupos")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.groups.subtitleOrg")}
          </p>
        </div>

        {canManageGroups ? (
          <button
            type="button"
            className="btn-primary inline-flex !h-10 shrink-0 items-center gap-2 !px-4"
            onClick={() => setCreateModalOpen(true)}
          >
            <TbUsersGroup size={18} aria-hidden />
            {intl.get("baas.groups.createGroup")}
          </button>
        ) : null}
      </header>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {groups.map((group) => (
          <div
            key={`${group.instanceId}-${group.id}`}
            role={canManageGroups ? "button" : undefined}
            tabIndex={canManageGroups ? 0 : undefined}
            className={`app-card flex flex-col p-5 transition duration-200 ${
              canManageGroups
                ? "cursor-pointer hover:-translate-y-0.5 hover:border-[#e7a73f]/50 hover:shadow-md dark:hover:border-brand-gold-mid/50"
                : ""
            }`}
            onClick={() => {
              if (canManageGroups) setEditingGroup(group);
            }}
            onKeyDown={(event) => {
              if (!canManageGroups) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setEditingGroup(group);
              }
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-brand-dark dark:text-gray-100">
                  {group.name}
                </h2>
                <p className="mt-1 text-xs font-medium text-brand-gold">
                  {instanceNameById[group.instanceId] ?? group.baasInstanceId ?? "—"}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {group.description}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400">
                  {intl.get("baas.groups.memberCount", { count: group.memberCount })}
                </span>
                {canManageGroups ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-brand-gold transition hover:bg-brand-gold/10"
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingGroup(group);
                    }}
                    aria-label={intl.get("baas.groups.editGroup")}
                  >
                    <TbPencil size={14} aria-hidden />
                    {intl.get("baas.groups.editGroup")}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                {intl.get("baas.groups.roles")}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(group.roles ?? [group.role].filter(Boolean)).map((roleItem) => (
                  <span
                    key={roleItem}
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${roleChipStyles[roleItem] ?? roleChipStyles.viewer}`}
                  >
                    {intl.get(roleLabelKey[roleItem] ?? roleItem)}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                {intl.get("baas.groups.permissions")}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {group.permissions.map((permission) => (
                  <span
                    key={permission}
                    className="rounded-md bg-brand-gold/10 px-2 py-0.5 font-mono text-xs text-brand-gold-dark dark:text-brand-gold-mid"
                  >
                    {permission}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {groups.length === 0 ? (
        <div className="app-card p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.groups.emptyOrg")}
        </div>
      ) : null}

      {canManageGroups ? (
        <BaasGroupCreateModal
          open={createModalOpen || Boolean(editingGroup)}
          group={editingGroup}
          onClose={handleModalClose}
          onSaved={handleSaved}
        />
      ) : null}
    </div>
  );
};
